import React, { useEffect, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import TextField from "@mui/material/TextField";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import InputLabel from "@mui/material/InputLabel";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import IconButton from "@mui/material/IconButton";
import AddIcon from "@mui/icons-material/Add";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";

import { updateMaterial, getMeasurementUnitsList } from "../../../../../services/Admin/MaterialsApi";

const normalizeKeywords = (value) => {
  if (Array.isArray(value)) {
    return value.map((keyword) => String(keyword).trim()).filter(Boolean);
  }

  return String(value || "")
    .split(/[\s,،;؛]+/)
    .map((keyword) => keyword.trim())
    .filter(Boolean);
};

const parseRules = (value) => {
  if (Array.isArray(value)) {
    const rules = value.map((rule) => String(rule).trim()).filter(Boolean);
    return rules.length ? rules : [""];
  }

  if (typeof value === "string") {
    try {
      const parsedValue = JSON.parse(value);
      if (Array.isArray(parsedValue)) {
        const rules = parsedValue.map((rule) => String(rule).trim()).filter(Boolean);
        return rules.length ? rules : [""];
      }
    } catch (error) {
      // fallback to line parsing below
    }

    const rules = value
      .split(/\r?\n/)
      .map((rule) => rule.trim())
      .filter(Boolean);

    return rules.length ? rules : [""];
  }

  return [""];
};

const normalizeRules = (value) => parseRules(value).filter(Boolean);

const EditeMaterialsDialog = ({ open, onClose, material = null, onUpdated }) => {
  const [name, setName] = useState("");
  const [unitId, setUnitId] = useState("");
  const [price, setPrice] = useState("");
  const [status, setStatus] = useState(true);
  const [maxUsage, setMaxUsage] = useState("");
  const [keywords, setKeywords] = useState("");
  const [rules, setRules] = useState([""]);

  const [units, setUnits] = useState([]);
  const [loadingUnits, setLoadingUnits] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  useEffect(() => {
    if (open) {
      setError("");
      setSuccess("");
      setName(material?.name || "");
      setUnitId(material?.measurment_unit_id ?? material?.measurement_unit_id ?? "");
      setPrice(material?.cost_price_per_unit ?? "");
      setStatus(Boolean(material?.status));
      setMaxUsage(material?.max_usage_per_product ?? "");
      setKeywords(Array.isArray(material?.keywords) ? material.keywords.join(", ") : (material?.keywords || ""));
      setRules(parseRules(material?.rules));
    }
  }, [open, material]);

  const handleRuleChange = (index, value) => {
    setRules((prev) => {
      const nextRules = [...prev];
      nextRules[index] = value;
      return nextRules;
    });
  };

  const addRule = () => {
    setRules((prev) => [...prev, ""]);
  };

  const removeRule = (index) => {
    setRules((prev) => {
      const nextRules = prev.filter((_, idx) => idx !== index);
      return nextRules.length ? nextRules : [""];
    });
  };

  useEffect(() => {
    const fetchUnits = async () => {
      setLoadingUnits(true);
      try {
        const res = await getMeasurementUnitsList();
        setUnits(res?.data || []);
      } catch (e) {
        // ignore
      } finally {
        setLoadingUnits(false);
      }
    };

    if (open) fetchUnits();
  }, [open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!material || !material.id) {
      setError("لا يوجد خامة محددة للتعديل");
      return;
    }

    setSaving(true);
    setError("");
    setSuccess("");
    try {
      const payload = {
        id: material.id,
        name: String(name || ""),
        name_en: "",
        measurement_unit_id: Number(unitId) || null,
        cost_price_per_unit: Number(price) || 0,
        status: Boolean(status),
        stock_quantity: null,
        max_usage_per_product: Number(maxUsage) || 0,
        min_stock_threshold: null,
        keywords: normalizeKeywords(keywords),
        rules: normalizeRules(rules),
      };

      const res = await updateMaterial(payload);
      setSuccess(res?.message || "تم التحديث بنجاح");
      if (typeof onUpdated === "function") onUpdated(res?.data || null);
      if (typeof onClose === "function") onClose();
    } catch (err) {
      setError(err?.message || "فشل في تحديث الخامة");
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setError("");
    setSuccess("");
    if (typeof onClose === "function") onClose();
  };

  return (
    <Dialog open={Boolean(open)} onClose={handleClose} fullWidth maxWidth="md">
      <DialogTitle>تعديل خامة</DialogTitle>
      <DialogContent>
        <Box component="form" onSubmit={handleSubmit} sx={{ display: "grid", gap: 2, mt: 1 }}>
          {error && <Alert severity="error">{error}</Alert>}
          {success && <Alert severity="success">{success}</Alert>}

          <TextField label="الاسم" value={name} onChange={(e) => setName(e.target.value)} required />

          <FormControl fullWidth>
            <InputLabel id="material-unit-label">وحدة القياس</InputLabel>
            <Select labelId="material-unit-label" value={unitId || ""} label="وحدة القياس" onChange={(e) => setUnitId(e.target.value)}>
              <MenuItem value="">اختر وحدة</MenuItem>
              {units.map((u) => (
                <MenuItem key={u.id} value={u.id}>{u.name} ({u.abbreviation})</MenuItem>
              ))}
            </Select>
          </FormControl>

          <TextField label="سعر الوحدة" value={price} onChange={(e) => setPrice(e.target.value)} />
          <TextField label="أقصى استخدام لكل منتج" value={maxUsage} onChange={(e) => setMaxUsage(e.target.value)} />

          <TextField label="الكلمات المفتاحية (مفصولة بفواصل)" value={keywords} onChange={(e) => setKeywords(e.target.value)} fullWidth />

          <Box sx={{ display: "grid", gap: 1 }}>
            {rules.map((rule, index) => (
              <Box key={index} sx={{ display: "grid", gridTemplateColumns: "auto 1fr auto", gap: 1, alignItems: "center" }}>
                <Box sx={{ fontWeight: 800, color: "text.secondary", minWidth: 28, textAlign: "center" }}>
                  {index + 1}
                </Box>
                <TextField
                  label={`قاعدة ${index + 1}`}
                  value={rule}
                  onChange={(e) => handleRuleChange(index, e.target.value)}
                  fullWidth
                />
                <IconButton aria-label="remove rule" onClick={() => removeRule(index)} disabled={rules.length === 1}>
                  <RemoveCircleOutlineIcon />
                </IconButton>
              </Box>
            ))}
            <Box>
              <Button size="small" startIcon={<AddIcon />} onClick={addRule}>
                إضافة قاعدة
              </Button>
            </Box>
          </Box>

          <FormControlLabel control={<Switch checked={Boolean(status)} onChange={(e) => setStatus(e.target.checked)} />} label="متاح" />

          <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
            <Button onClick={handleClose} color="inherit">إلغاء</Button>
            <Button type="submit" variant="contained" disabled={saving || loadingUnits}>
              {saving ? <CircularProgress size={20} /> : "حفظ"}
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default EditeMaterialsDialog;
